<template>
    <div class="chat-message" :class="{ 'user-message': isUser, 'assistant-message': isAssistant }">
        <div class="message-avatar">
            <span class="material-symbols-outlined">
                {{ isUser ? 'person' : 'smart_toy' }}
            </span>
        </div>
        <div class="message-content">
            <div class="message-bubble" :class="{ 'user-bubble': isUser, 'assistant-bubble': isAssistant }">
                <div class="message-text" v-html="formattedContent"></div>
                <div v-if="message.tool_used" class="message-tool">
                    <span class="material-symbols-outlined">build</span>
                    <span>{{ message.tool_used }}</span>
                </div>
            </div>
            <div class="message-time">
                {{ formatTime(message.created_at) }}
            </div>
        </div>
    </div>
</template>

<script>
/**
 * ChatMessage Component
 * Renders individual chat messages with support for user and assistant roles.
 * Handles markdown-like formatting and tool usage indicators.
 */
export default {
    name: 'ChatMessage',
    props: {
        /** @type {Object} Chat message data */
        message: {
            type: Object,
            required: true,
        },
    },
    computed: {
        /** Determines if the message is from the user */
        isUser() {
            return this.message.role === 'user'
        },
        /** Determines if the message is from the AI assistant */
        isAssistant() {
            return this.message.role === 'assistant'
        },
        /** Formats message content with markdown-like styling */
        formattedContent() {
            let content = this.message.content
            content = this.convertTablesToList(content)
            content = this.escapeHtml(content)
            content = content.replace(/\n/g, '<br>')
            content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            content = content.replace(/\*(.*?)\*/g, '<em>$1</em>')
            content = content.replace(/`(.*?)`/g, '<code>$1</code>')
            content = content.replace(/^#### (.+)$/gm, '<h4>$1</h4>')
            content = content.replace(/^### (.+)$/gm, '<h3>$1</h3>')
            content = content.replace(/^## (.+)$/gm, '<h2>$1</h2>')
            content = content.replace(/^- (.+)(<br>|$)/gm, '<span class="bullet-item">• $1</span>$2')
            content = content.replace(/^(---|\*\*\*)$/gm, '<hr>')
            content = content.replace(/• (.*?)(<br>|$)/g, '<span class="bullet-item">• $1</span>$2')
            return content
        },
    },
    methods: {
        /**
         * Convert markdown table syntax to bullet list when table is too wide
         * Detects pipe-delimited rows, extracts header as labels, renders data rows as bullets
         * @param {string} text - Raw message content
         * @returns {string} Content with tables converted to bullet lists
         */
        convertTablesToList(text) {
            const lines = text.split('\n')
            const result = []
            let i = 0

            while (i < lines.length) {
                const line = lines[i].trim()

                if (line.startsWith('|') && (line.match(/\|/g) || []).length >= 3) {
                    const tableRows = []
                    while (i < lines.length && lines[i].trim().startsWith('|')) {
                        tableRows.push(lines[i])
                        i++
                    }

                    if (tableRows.length >= 3) {
                        const headers = tableRows[0]
                            .split('|')
                            .map(c => c.trim())
                            .filter(c => c.length > 0)

                        const separatorLine = tableRows[1]
                        const isSeparator = separatorLine.match(/^[\s\|:-]+$/) && separatorLine.includes('-')

                        const dataStartIndex = isSeparator ? 2 : 1

                        if (headers.length > 0 && dataStartIndex < tableRows.length) {
                            if (headers.length >= 3) {
                                for (let r = dataStartIndex; r < tableRows.length; r++) {
                                    const cells = tableRows[r]
                                        .split('|')
                                        .map(c => c.trim())
                                        .filter(c => c.length > 0)

                                    if (cells.length > 0) {
                                        const parts = cells.map((cell, idx) => {
                                            const label = idx < headers.length ? headers[idx] : `Col ${idx + 1}`
                                            return `${label}: ${cell}`
                                        })
                                        result.push(`• ${parts.join(' | ')}`)
                                    }
                                }
                            } else {
                                for (let r = dataStartIndex; r < tableRows.length; r++) {
                                    const cells = tableRows[r]
                                        .split('|')
                                        .map(c => c.trim())
                                        .filter(c => c.length > 0)
                                    cells.forEach(cell => result.push(`• ${cell}`))
                                }
                            }
                        } else {
                            for (let r = dataStartIndex; r < tableRows.length; r++) {
                                const cells = tableRows[r]
                                    .split('|')
                                    .map(c => c.trim())
                                    .filter(c => c.length > 0)
                                cells.forEach(cell => result.push(`• ${cell}`))
                            }
                        }
                    } else {
                        for (let j = 0; j < tableRows.length; j++) {
                            result.push(tableRows[j])
                        }
                    }
                } else {
                    result.push(lines[i])
                    i++
                }
            }

            return result.join('\n')
        },

        /**
         * Escapes HTML special characters to prevent XSS
         * @param {string} text - Raw text to escape
         * @returns {string} Escaped HTML-safe text
         */
        escapeHtml(text) {
            const div = document.createElement('div')
            div.textContent = text
            return div.innerHTML
        },
        /**
         * Formats timestamp for display
         * @param {string|null} timestamp - ISO timestamp string
         * @returns {string} Formatted time (HH:MM)
         */
        formatTime(timestamp) {
            if (!timestamp) return ''
            const date = new Date(timestamp)
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        },
    },
}
</script>

<style scoped>
.chat-message {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.user-message {
    flex-direction: row-reverse;
}

.message-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.user-message .message-avatar {
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-container) 100%);
    color: white;
}

.assistant-message .message-avatar {
    background: var(--color-surface-container-low);
    color: var(--color-primary);
}

.message-avatar .material-symbols-outlined {
    font-size: 20px;
}

.message-content {
    max-width: 80%;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.user-message .message-content {
    align-items: flex-end;
}

.assistant-message .message-content {
    align-items: flex-start;
}

.message-bubble {
    padding: 12px 16px;
    border-radius: 18px;
    font-size: 14px;
    line-height: 1.5;
}

.user-bubble {
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-container) 100%);
    color: var(--color-on-primary);
    border-bottom-right-radius: 4px;
}

.assistant-bubble {
    background: var(--color-surface-container-low);
    color: var(--color-on-surface);
    border-bottom-left-radius: 4px;
}

.message-text {
    word-wrap: break-word;
    white-space: pre-wrap;
    overflow-x: auto;
    max-width: 100%;
}

.message-text :deep(h2) {
    font-size: 1.1em;
    font-weight: 700;
    margin: 16px 0 8px;
    color: var(--color-on-surface);
    font-family: 'Plus Jakarta Sans', sans-serif;
}

.message-text :deep(h3) {
    font-size: 1em;
    font-weight: 600;
    margin: 12px 0 6px;
    color: var(--color-on-surface);
}

.message-text :deep(h4) {
    font-size: 0.95em;
    font-weight: 600;
    margin: 8px 0 4px;
    color: var(--color-on-surface-variant);
}

.message-text :deep(hr) {
    border: none;
    border-top: 1px solid var(--color-outline-variant);
    margin: 12px 0;
}

.message-text :deep(code) {
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 13px;
}

.user-bubble .message-text :deep(code) {
    background: rgba(255, 255, 255, 0.2);
}

.message-tool {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(128, 117, 105, 0.2);
    font-size: 11px;
    color: var(--color-outline);
}

.message-tool .material-symbols-outlined {
    font-size: 14px;
}

.message-time {
    font-size: 11px;
    color: var(--color-outline);
    padding: 0 4px;
}

:deep(.bullet-item) {
    display: block;
    margin: 4px 0;
}
</style>
